import {
  DatepickerSimple,
  Select,
  Timestamp,
  BankFileConversionRail,
  FileConversionConfiguration,
  objectEntries,
  FileConversionService,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FieldData } from '@/components/Form/types';
import * as Yup from 'yup';
import { format } from 'date-fns';

export type ProcessFileFormValues = {
  Service: FileConversionService;
  PaymentRail: BankFileConversionRail | '';
  FileConversionConfigurationID: string;
  ValueDate: string;
  AlternatePaymentRail: BankFileConversionRail | '';
};

export const useprocessFileFormFields = ({
  configurations,
  configurationsLoading,
}: {
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const getBankOptions = () => {
    return configurations.map((config) => {
      return {
        value: config.FileConversionConfigurationID,
        label: config.AccountNumber
          ? `${config.FinancialInstitution} - ${config.AccountNumber}`
          : config.FinancialInstitution,
      };
    });
  };

  const getProductOptions = (conversionID: string, service: string) => {
    // Find the configuration for the selected bank
    const bankConfig = configurations.find((config) => config.FileConversionConfigurationID === conversionID);
    if (!bankConfig) return [];

    // Service key is now lowercase in the new structure
    const serviceKey = service.toLowerCase();
    const validServiceKey = serviceKey === 'credit' || serviceKey === 'debit' ? serviceKey : undefined;
    const railsObj = validServiceKey ? bankConfig.PaymentRails?.[validServiceKey] : undefined;
    if (!railsObj || typeof railsObj !== 'object') return [];

    // Map payment rail codes to readable names
    const railNames: Record<BankFileConversionRail, string> = {
      eft: tGlobal('PaymentRail.eft'),
      vopayinstant: tGlobal('PaymentRail.vopayInstant'),
      ach: tGlobal('PaymentRail.ach'),
      rtp: tGlobal('PaymentRail.rtp'),
      fednow: tGlobal('PaymentRail.fedNow'),
      fedwire: tGlobal('PaymentRail.fedwire'),
      interac: tGlobal('PaymentRail.Interac'),
      aft: tGlobal('PaymentRail.AFT'),
      creditcard: tGlobal('PaymentRail.creditcard'),
      debitcard: tGlobal('PaymentRail.debitcard'),
      oct: tGlobal('PaymentRail.OCT'),
    };

    // Only include rails with DownloadEnabled true
    return objectEntries(railsObj).map(([rail, value]) => ({
      isDisabled: !value.SubmissionEnabled,
      value: rail,
      label: railNames[rail],
    }));
  };

  const convertTo: FieldData<ProcessFileFormValues>[] = [
    {
      fieldName: 'FileConversionConfigurationID',
      label: () => t('processorLabel', { defaultValue: 'Processor' }),
      getInputState: () => 'optional',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        // If only one option, set it as selected by default
        const options = getBankOptions();
        return (
          <Select
            version="2"
            {...getFormikData<ProcessFileFormValues['FileConversionConfigurationID']>({})}
            options={options}
            placeholder=""
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'PaymentRail',
      label: () => t('productLabel', { defaultValue: 'Payment Rail' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);

        return (
          <Select
            version="2"
            {...getFormikData<ProcessFileFormValues['PaymentRail']>({})}
            options={productOption}
            loading={configurationsLoading}
            disabled={productOption.length === 0}
            disableOptionWhen={(o) => o.isDisabled}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'AlternatePaymentRail',
      label: () => t('alternativePaymentRailLabel', { defaultValue: 'Fallback Payment Rail' }),
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);

        return (
          <Select
            version="2"
            {...getFormikData<ProcessFileFormValues['AlternatePaymentRail']>({})}
            options={productOption}
            disabled={productOption.length === 0}
            disableOptionWhen={(o) => o.isDisabled}
            placeholder=""
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'ValueDate',
      label: () => t('valueDateLabel', { defaultValue: 'Value Date' }),
      getInputState: () => 'required',
      serializeField: (values) => {
        const endDate = new Date(Number(values.ValueDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        const data = getFormikData<ProcessFileFormValues['ValueDate']>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            position="topLeft"
            {...data}
            min={new Date().getTime() as Timestamp}
            isDisabled={data.disabled}
          />
        );
      },
    },
  ];

  return { convertTo };
};
